import { emit, on } from "./eventEmitter.js";
import { playWav, stopAllWav } from "./playBase64audio.js";
import { initiateWidgetCall } from "./initiateWidgetCall.js";
import {
  audioBufferToWav,
  float32ArrayToAudioBuffer,
  convertBlobToBase64,
  mergeBase64Audios,
  saveAudio,
} from "./processAudioBlob.js";

(function () {
  let socket;
  let deepgramSocket;
  let myVad;
  let isCalling = false;
  let audioContext;
  let mediaStream;
  let mediaRecorderRef;
  let conversation = [];
  const audioQueue = [];
  let fullAudioChunks = [];
  let transcript = "";
  let isPlaying = false;
  let call_id = "";
  let isProcessing = true;
  let isReady = false;

  async function playNextAudio() {
    if (isPlaying || audioQueue.length === 0) return;

    isPlaying = true;
    const audioChunk = audioQueue.shift();

    if (!audioChunk) {
      isPlaying = false;
      return;
    }

    playWav(
      audioChunk,
      () => {
        isPlaying = false;
        if (audioQueue.length > 0) {
          playNextAudio();
        }
      },
      (err) => {
        console.error("Error playing audio:", err);
        isPlaying = false;
        playNextAudio();
        emit("error", err);
      }
    );
  }

  function addAudioChunk(chunk) {
    audioQueue.push(chunk);
    if (!isPlaying) {
      playNextAudio();
    }
  }

  async function startCall(widgetTheme) {
    if (isCalling) return;
    isCalling = true;
    emit("dialing");
    console.log("widgetTheme", widgetTheme);
    try {
      const resp = await initiateWidgetCall(widgetTheme?.id);
      if (resp?.status) {
        call_id = resp?.call_id;
        socket = new WebSocket(
          `wss://indigenius.shop/web-call/connection?assistant_id=${widgetTheme?.Assistant?.id}&call_id=${resp?.call_id}`
        );

        socket.onopen = () => {
          emit("connected");
        };

        socket.onmessage = (event) => {
          const eventData = JSON.parse(event?.data);
          isProcessing = false;
          if (eventData?.event === "audio-response") {
            conversation.push({
              role: "assistant",
              content: eventData?.ai_response,
            });
            addAudioChunk(eventData?.audioChunk);
            fullAudioChunks.push(eventData?.audioChunk);
          } else if (eventData?.event == "ready" && !isReady) {
            isReady = true;
            sendApplicationRequest({
              first_message:
                widgetTheme?.Assistant?.first_message ||
                `Hello there, My name is ${widgetTheme?.Assistant?.name}  and I am an AI agent!`,
            });

            conversation.push({
              role: "assistant",
              content:
                widgetTheme?.first_message ||
                `Hello there, My name is ${widgetTheme?.Assistant?.name}  and I am an AI agent!`,
            });
          }
        };
        socket.onclose = () => emit("ended");
        socket.onerror = (err) => emit("error", err);

        await initVad();
        isProcessing = true;

        // Connect to Deepgram WebSocket
        deepgramSocket = new WebSocket(
          `wss://api.deepgram.com/v1/listen?model=nova-2&language=en&smart_format=true&punctuate=true&diarize=true&endpointing=${1000}`,
          ["token", "d04e7988f576755bdedec0da833e9e042d9d1c09"]
        );

        function sendApplicationRequest(message) {
          if (socket && socket.readyState === WebSocket.OPEN && isReady) {
            socket.send(JSON.stringify(message));
            isProcessing = true;
          } else {
            console.error("WebSocket is not open. Unable to send message.");
          }
        }

        deepgramSocket.onclose = () =>
        deepgramSocket.onerror = (err) => emit("error", err);

        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        mediaRecorderRef = new MediaRecorder(mediaStream, {
          mimeType: "audio/webm",
        });

        deepgramSocket.onopen = () => {
          mediaRecorderRef?.addEventListener("dataavailable", (event) => {
            if (event.data.size > 0 && deepgramSocket?.readyState === 1) {
              deepgramSocket.send(event.data);
            }
          });
          if (mediaRecorderRef) {
            mediaRecorderRef.start(500);
          }
        };

        deepgramSocket.onmessage = async (message) => {
          const received = JSON.parse(message.data);
          transcript = received.channel?.alternatives[0]?.transcript;
          handleDeepgramMessage(message);
        };

        function handleDeepgramMessage(event) {
          const data = JSON.parse(event.data);
          transcript = data.channel?.alternatives?.[0]?.transcript;

          if (transcript) {
            emit("transcript", transcript);

            if (socket && socket.readyState === WebSocket.OPEN) {
              if (!isPlaying) {
                conversation.push({
                  role: "user",
                  content: transcript,
                });
                sendApplicationRequest({
                  role: "user",
                  prompt: JSON.stringify(conversation),
                });
              } else {
                transcript = "";
              }
            } else {
              transcript = "";
            }
          }
        }
      }
    } catch (error) {
      emit("error", error);
      endCall();
    }
  }

  async function initVad() {
    try {
      myVad = await vad.MicVAD.new({
        onSpeechEnd: async (audio) => {
          if (!isPlaying && !isProcessing) {
            const audioBuffer = await float32ArrayToAudioBuffer(audio, 16000);
            const wavBlob = await audioBufferToWav(audioBuffer);
            const base64Audio = await convertBlobToBase64(wavBlob);
            fullAudioChunks.push(base64Audio);
          }
        },
      });
      myVad.start();
    } catch (error) {
      emit("error", error);
    }
  }

  loadVad();

  function endCall() {
    if (!isCalling) return;
    isCalling = false;

    if (socket) socket.close();
    if (deepgramSocket) deepgramSocket.close();
    if (mediaRecorderRef) mediaRecorderRef.stop();
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
    }
    isReady = false;
    stopAllWav()
    if (audioContext) audioContext.close();
    conversation = [];
    if (myVad) {
      myVad.destroy();
    }

    if (fullAudioChunks?.length) {
      const audioDataArray = fullAudioChunks.map((base64, index) => ({
        base64,
        interval: index === 0 ? 0 : 0,
      }));
      (async () => {
        const mergedAudioBuffer = await mergeBase64Audios(audioDataArray);
        //  if (activeCallId && !assistantDetails?.hipaa) {
        await saveAudio(mergedAudioBuffer, call_id);
        //  }
        fullAudioChunks = [];
      })();
    }

    emit("ended");
  }

  // Expose functions & event listener globally
  window.myCdialWidget = {
    startCall,
    endCall,
    get isCalling() {
      return isCalling;
    },
    on,
  };
})();

async function loadVad() {
  return new Promise((resolve, reject) => {
    const script1 = document.createElement("script");
    const script2 = document.createElement("script");

    script1.src =
      "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.14.0/dist/ort.js";
    script2.src =
      "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.22/dist/bundle.min.js";

    script1.onload = () => {
      document.head.appendChild(script2);
    };

    script2.onload = () => {
      if (window.vad) {
        resolve(window.vad);
      } else {
        reject(new Error("Failed to load VAD"));
      }
    };

    script1.onerror = reject;
    script2.onerror = reject;

    document.head.appendChild(script1);
  });
}
